import { useEffect } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { useNavigate } from 'react-router-dom'

import { useI18n } from 'builder/hooks/useI18n'
import { useDocument } from 'builder/hooks/useDocument'
import { useGetDocuments } from 'builder/hooks/useGetDocuments'
import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'

import { DocumentTypes } from 'builder/modules/constants'
import * as SimpleForm from 'builder/components/SimpleForm'
import { selectors } from 'builder/modules/autoApply/autoApplyModule'

import CoverLetterIcon from '../../assets/CoverLetterIcon.png'
import { TailoringToggleCard } from '../TailoringToggleCard'
import { ResumePreview } from '../ResumePreview/ResumePreview'
import { Routes, StartCompleteScreenRoute } from '../../constants'
import { usePreviewDocuments } from '../../hooks/usePreviewDocuments'
import { TailorResume } from './CoverLetterContainer'

const TRANSLATION = 'builder.auto_apply.form.cover_letter'
const TAILORING_CARD = `${TRANSLATION}.tailoring_cv_toggle_card`
type Props = {
  form: UseFormReturn<TailorResume>
  setShowResumeSelect: (show: boolean) => void
}

export const CoverLetterForm = ({ form, setShowResumeSelect }: Props) => {
  const { register, watch, setValue } = form
  const formState = useSelector(selectors.form)
  const { i18n } = useI18n()
  const navigate = useNavigate()
  const { features } = useFeaturesConfig()
  const { navigateToDocumentPreview, navigateToTrDocumentPreview } = usePreviewDocuments()
  const {
    data: documents,
    isLoading,
    isFetching,
  } = useGetDocuments({
    filterType: 'cover_letter',
    fetch: features.aaTailoring === 'v1',
  })
  const trDocument = useDocument(
    formState.resumeType === DocumentTypes.trDocument ? formState.resumeId : null,
  )

  const trCoverLetter = documents?.[0]
  const trNewFlow = features.aaTailoring === 'v1'

  const onResumeSelect = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    trackInternalEvent('aa_click_change_resume', { label: 'total_job_search_solution' })
    if (trNewFlow) {
      setShowResumeSelect(true)
    } else {
      navigate(`/auto-apply/${StartCompleteScreenRoute.RESUME_SELECT}`)
    }
  }

  const getSelectedResumeId = () => {
    if (trNewFlow) {
      return formState.selectedCoverLetterId || trCoverLetter?.id || null
    }
    return formState.resumeId
  }

  const getSelectedResumeType = () => {
    if (trNewFlow && trCoverLetter?.type) {
      return trCoverLetter.type as DocumentTypes
    }
    return formState.resumeType as DocumentTypes
  }

  const onPreviewDocument = () => {
    const { resumeId, resumeType } = formState
    if (!resumeId || !resumeType) return

    trackInternalEvent('open_full_screen_resume_preview', { label: 'auto_apply' })
    const isTrDocument = resumeType === DocumentTypes.trDocument

    if (isTrDocument) {
      if (!trNewFlow && trDocument?.resume_id) {
        navigateToDocumentPreview({ id: trDocument?.resume_id, path: Routes.COVER_LETTER })
      } else {
        navigateToTrDocumentPreview({
          id: getSelectedResumeId() || resumeId,
          isTailoringEnabled: watch('autoTailoringCl'),
          disableEdit: !!getSelectedResumeId(),
          path: Routes.COVER_LETTER,
        })
      }
    } else {
      navigateToDocumentPreview({ id: resumeId, path: Routes.COVER_LETTER })
    }
  }

  const onSwitchToggle = (value: boolean) => {
    setValue('autoTailoringCl', value)
  }

  useEffect(() => {
    if (trNewFlow && trCoverLetter?.id) {
      setValue('selectedCoverLetterId', trCoverLetter.id)
    }
  }, [trNewFlow, trCoverLetter?.id, setValue])

  return (
    <>
      <SimpleForm.Row position="relative">
        <ResumePreview
          isLoading={isFetching && isLoading}
          resumeUrl={formState.resumeUrl}
          upladedAt={formState.resumeUploadedAt}
          showChangeButton={trNewFlow && !!documents?.length && documents?.length > 1}
          selectedResumeId={getSelectedResumeId()}
          selectedResumeType={getSelectedResumeType()}
          onClickPreview={onPreviewDocument}
          handleBuilderResumeChange={onResumeSelect}
        />
      </SimpleForm.Row>

      <SimpleForm.Row position="relative">
        <TailoringToggleCard
          {...register('autoTailoringCl')}
          image={
            <img
              style={{ padding: '12.602px 0 16.498px' }}
              height="98"
              width="98"
              src={CoverLetterIcon}
            />
          }
          checked={watch('autoTailoringCl')}
          onToggle={onSwitchToggle}
          titleText={i18n.t(`${TAILORING_CARD}.title`)}
          subtitleText={i18n.t(`${TAILORING_CARD}.subtitle${trNewFlow ? '_2' : ''}`)}
          toggleText={i18n.t(`${TAILORING_CARD}.toggle_text`)}
        />
      </SimpleForm.Row>
    </>
  )
}
